import {
  ColorScheme,
  DesignToken,
  ThemeName,
} from '@design/design-tokens/themes/theme-names';
import {
  hslToHex,
  isHslColor,
  rgbToHex,
} from '@design/utils/color-conversion/color-conversion';

const RGB_FUNC =
  /^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)(?:\s*[,/]\s*([\d.]+%?))?/;

const parseRgbAlpha = (alpha: string | undefined) => {
  if (alpha === undefined) {
    return 1;
  }

  return alpha.endsWith('%')
    ? Number.parseFloat(alpha) / 100
    : Number.parseFloat(alpha);
};

const toOpaqueHex = (color: string) => {
  if (color.startsWith('#')) {
    return color;
  }

  if (isHslColor(color)) {
    return hslToHex(color);
  }

  const rgb = RGB_FUNC.exec(color);
  if (!rgb) {
    return color;
  }

  return rgbToHex({
    r: Number(rgb[1]),
    g: Number(rgb[2]),
    b: Number(rgb[3]),
  });
};

const toCssColor = (color: string) => {
  const rgb = RGB_FUNC.exec(color);
  if (rgb && parseRgbAlpha(rgb[4]) < 1) {
    return color;
  }

  return toOpaqueHex(color);
};

const isConcreteColor = (value: string) =>
  Boolean(value) && !value.startsWith('var(');

const resolveFromElement = (element: Element, colorToken: string) => {
  const specified = getComputedStyle(element)
    .getPropertyValue(colorToken)
    .trim();

  if (isConcreteColor(specified)) {
    return specified;
  }

  const sample = document.createElement('span');
  sample.style.color = `var(${colorToken})`;
  element.appendChild(sample);
  const resolved = getComputedStyle(sample).color.trim();
  sample.remove();

  if (isConcreteColor(resolved)) {
    return resolved;
  }

  return specified;
};

const readCssThemeColor = (
  colorToken: string,
  themeName: ThemeName
): string => {
  if (typeof document === 'undefined' || !colorToken.startsWith('--')) {
    return '';
  }

  if (document.documentElement.getAttribute('data-theme') === themeName) {
    return resolveFromElement(document.documentElement, colorToken);
  }

  const host = document.createElement('span');
  host.setAttribute('data-theme', themeName);
  // Radix --gray-* / --blue-* are scoped to .light / .dark, not data-theme.
  host.className = themeName.startsWith(`${ColorScheme.Dark}_`)
    ? ColorScheme.Dark
    : ColorScheme.Light;
  host.setAttribute('aria-hidden', 'true');
  host.style.cssText =
    'position:absolute;left:-99999px;width:0;height:0;overflow:hidden;pointer-events:none';
  document.body.appendChild(host);
  const resolved = resolveFromElement(host, colorToken);
  host.remove();

  return resolved;
};

export const getThemeColorInHex = (
  themeName: ThemeName,
  designToken: DesignToken | string
) => {
  const token = String(designToken);
  const themeColor = readCssThemeColor(token, themeName);
  return themeColor ? toOpaqueHex(themeColor) : themeColor;
};

export const getThemeColor = (
  colorToken: DesignToken | string,
  themeName: ThemeName
): string => {
  const token = String(colorToken);
  const themeColor = readCssThemeColor(token, themeName);
  return themeColor ? toCssColor(themeColor) : token;
};
